// Read-only counterpart to VideoUpload. Mirrors the phase machine and skeleton
// style of VideoUpload but omits upload, camera, and delete functionality.
// Keep the two in sync when changing playback behaviour or skeleton appearance.
import './VideoViewer.css';

import React, {
  useEffect,
  useState,
} from 'react';

import {
  getStreamPlaybackToken,
  getStreamVideoStatus,
} from '../../../services/streamService';
import SpinnerIcon from '../SpinnerIcon';

export interface VideoViewerProps {
  videoUid: string;
  label?: string;
}

type Phase = 'checking' | 'processing' | 'ready' | 'error';

const POLL_INTERVAL_MS = 3000;

const VideoViewer: React.FC<VideoViewerProps> = ({ videoUid, label = 'Athlete video' }) => {
  const [phase, setPhase] = useState<Phase>('checking');
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [iframeExpanded, setIframeExpanded] = useState(false);
  const [aspect, setAspect] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const check = async (): Promise<void> => {
      try {
        const status = await getStreamVideoStatus(videoUid);
        if (cancelled) return;
        if (status.width && status.height) {
          setAspect({ width: status.width, height: status.height });
        }
        if (status.readyToStream) {
          const { iframeUrl: url } = await getStreamPlaybackToken(videoUid);
          if (cancelled) return;
          setIframeUrl(url);
          setPhase('ready');
        } else {
          setPhase('processing');
        }
      } catch {
        if (!cancelled) setPhase('error');
      }
    };
    void check();
    return () => { cancelled = true; };
  }, [videoUid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase !== 'processing') return;
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const status = await getStreamVideoStatus(videoUid);
        if (cancelled || !status.readyToStream) return;
        if (status.width && status.height) {
          setAspect({ width: status.width, height: status.height });
        }
        const { iframeUrl: url } = await getStreamPlaybackToken(videoUid);
        if (cancelled) return;
        setIframeUrl(url);
        setPhase('ready');
      } catch {
        // transient error; next tick retries
      }
    };
    const id = window.setInterval(() => { void poll(); }, POLL_INTERVAL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [phase, videoUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const aspectStyle: React.CSSProperties = aspect
    ? { aspectRatio: `${aspect.width} / ${aspect.height}` }
    : { aspectRatio: '9 / 16' };

  if (phase === 'error') {
    return <div className="video-viewer-error">Video unavailable</div>;
  }

  if (phase !== 'ready' || !iframeUrl) {
    return (
      <div className="video-viewer-skeleton" style={aspectStyle}>
        <SpinnerIcon className="video-viewer-spinner" />
      </div>
    );
  }

  if (!iframeExpanded) {
    return (
      <button
        type="button"
        className="video-viewer-thumb-button"
        onClick={() => setIframeExpanded(true)}
        aria-label={`Play ${label}`}
      >
        <span className="video-viewer-thumb-frame" style={aspectStyle}>
          <iframe
            className="video-viewer-thumb-iframe"
            src={`${iframeUrl}?muted=true&preload=metadata&controls=false`}
            allow="accelerometer; gyroscope; encrypted-media;"
            title={label}
            tabIndex={-1}
          />
          <span className="video-viewer-play-overlay" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="28" height="28">
              <path d="M8 5v14l11-7z" fill="currentColor" />
            </svg>
          </span>
        </span>
      </button>
    );
  }

  return (
    <div className="video-viewer-player" style={aspectStyle}>
      <iframe
        className="video-viewer-iframe"
        src={`${iframeUrl}?autoplay=true&muted=true&preload=metadata`}
        allow="accelerometer; gyroscope; encrypted-media;"
        allowFullScreen
        title={label}
      />
    </div>
  );
};

export default VideoViewer;
